import React, { useCallback, useContext, useEffect } from 'react';

import { updateDatasetDoc } from 'js/actions';
import { renderFeedApiView } from 'js/components/data_sets/cards/DataSetCardOverviewTab/utils/renderFeedApiView';
import DataSetDataBrowserContainer from 'js/components/data_sets/cards/DataSetDataBrowserContainer';
import DocEditor from 'js/components/elements/editors/DocEditor';
import { GET_DATA_SAMPLES_REQUEST } from 'js/constants/actionTypes';
import { RESOURCE_TYPE_DATA_SET } from 'js/constants/resourceTypes';
import { SchemaEditorViewModeType } from 'js/constants/schemaEditor';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { isReadOnly } from 'js/lib/ACLUtils';
import DateUtils from 'js/lib/DateUtils';
import RoutePrefixContext from 'new/contexts/RoutePrefixContext';
import { DatasetInfoPanel } from 'new/elements/DatasetInfoPanel';
import { useDatasetApiKey } from 'new/hooks/adapters/dataset/useDatasetApiKey';
import { useFetchDatasetDoc } from 'new/hooks/adapters/docs';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { useDataSetAdapter } from 'new/state/data_set/adapter/useDataSetAdapter';
import { Dataset } from 'new/components/marketplace/components/DataProductsDrawer/types';

import styles from './OverviewTab.module.scss';
import { isSharedDataset } from 'js/lib/URLUtils';

interface OverviewTabProps {
  dataset: Dataset;
}

/**
 * OverviewTab component for the DataProductsDrawer and SharedDataset
 */
export const OverviewTab = ({ dataset }: OverviewTabProps) => {
  const dispatch = useAppDispatch();
  const { requestDataSetById } = useDataSetAdapter();
  const isLoadingSamples = useIsLoading(GET_DATA_SAMPLES_REQUEST);
  const routePrefix = useContext(RoutePrefixContext) as string;

  const { doc, isLoadingDoc } = useFetchDatasetDoc(dataset.id);
  const { apiKeys, isLoading, isSyncable, generateApiKey } = useDatasetApiKey(dataset);

  useEffect(() => {
    const fetchDataset = async () => {
      if (!dataset?.id) return;
      try {
        await requestDataSetById(dataset.id);
      } catch (error) {
        console.error('Failed to fetch dataset:', error);
      }
    };

    /**
     * shared_dataset comes from the API with basic information,
     * so we need to fetch the full dataset to get the latest information.
     */
    fetchDataset();
  }, [dataset?.id, requestDataSetById]);

  const handleUpdateDoc = useCallback(
    (id: number, content: string) => {
      dispatch(updateDatasetDoc(id, content));
    },
    [dispatch],
  );

  const isReadOnlyResource = isReadOnly(dataset); // ["collaborator"] => true, [] => true, ["admin"] => false
  const isNotReadOnlyResource = !isReadOnlyResource;
  const isSharedResource = isSharedDataset(dataset);

  const canShowFeedApiSection = isNotReadOnlyResource && isSyncable;
  const canEditDocResource = isNotReadOnlyResource && !isSharedResource;
  const canShowDocEditor = isLoadingDoc || (doc !== null && doc !== '');

  const feedApiSectionContent = canShowFeedApiSection
    ? renderFeedApiView({
        config: {
          isSyncable,
          data: dataset,
          apiKeys: apiKeys || {},
          isLoading: { dataFeedApiKey: isLoading },
          generateNexsetDataLink: generateApiKey,
          routePrefix,
        },
      })
    : null;

  const createdDate = DateUtils.convertDateStringToFormat(dataset?.created_at);

  return (
    <div className={styles.detailLayout}>
      <div className={styles.mainCol}>
        <div className="detail-view">
          <div className="tab-content">
            {canShowDocEditor && (
              <DocEditor
                data={dataset}
                doc={doc}
                isLoading={isLoadingDoc}
                readOnly={!canEditDocResource}
                resourceType={RESOURCE_TYPE_DATA_SET}
                updateDoc={canEditDocResource ? handleUpdateDoc : undefined}
              />
            )}
            <h2 style={{ paddingLeft: 'var(--spacing-lg)' }}>Schema & Samples</h2>
            <DataSetDataBrowserContainer
              data={dataset}
              isLoadingSamples={isLoadingSamples}
              defaultViewMode={SchemaEditorViewModeType.TABLE}
            />
          </div>
        </div>
      </div>

      <DatasetInfoPanel
        datasetId={dataset.id}
        createdDate={createdDate}
        datasetSyncInfo={feedApiSectionContent}
      />
    </div>
  );
};
